/**
 * API client for the Nexus Worker.
 *
 * All paths are relative to current origin; in dev mode Vite proxies
 * /api/* to the Worker (see vite.config.ts).
 */

const API = "/api";


/* ---------- types (mirror the Worker's response shapes) ---------- */

export interface Session {
  id: string;
  instance_id: string;
  title: string;
  created_at: string;
  updated_at: string;
}

export interface Message {
  id: number;
  session_id: string;
  role: "user" | "assistant";
  content: string;
  tool_calls: Array<{ name: string; input: unknown; result_summary: string; ok?: boolean }>;
  citations: string[];
  tokens_in: number;
  tokens_out: number;
  created_at: string;
  /** Files sent with a user message (shown as chips in the bubble). */
  attachments?: { name: string }[];
}

export interface SynapsePage {
  id: string;
  header: Record<string, unknown>;
  body: string;
  path: string;
}

export interface HealthResponse {
  ok: boolean;
  worker_version: string;
  bindings: {
    r2_corpus: boolean;
    kv: boolean;
    anthropic_key_present: boolean;
  };
  synapse_bundle: { total_pages: number; instances: number };
  available_instances: string[];
  phase: string;
}


/* ---------- low-level fetch wrapper ---------- */

async function api<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const r = await fetch(`${API}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init.headers || {}),
    },
  });
  if (!r.ok) {
    const text = await r.text();
    throw new Error(`API ${r.status} ${path}: ${text.slice(0, 300)}`);
  }
  return r.json() as Promise<T>;
}


/* ---------- typed endpoints ---------- */

export function getHealth(): Promise<HealthResponse> {
  return api("/health");
}

export function listSessions(
  instance: string,
): Promise<{ sessions: Session[] }> {
  return api(`/${instance}/sessions`);
}

export function createSession(
  instance: string,
  title?: string,
): Promise<{ session: Session }> {
  return api(`/${instance}/sessions`, {
    method: "POST",
    body: JSON.stringify({ title: title || "New chat" }),
  });
}

export function getSession(
  instance: string,
  sid: string,
): Promise<{ session: Session; messages: Message[] }> {
  return api(`/${instance}/sessions/${sid}`);
}

export function renameSession(
  instance: string,
  sid: string,
  title: string,
): Promise<{ ok: boolean }> {
  return api(`/${instance}/sessions/${sid}`, {
    method: "PATCH",
    body: JSON.stringify({ title }),
  });
}

export function deleteSession(
  instance: string,
  sid: string,
): Promise<{ ok: boolean }> {
  return api(`/${instance}/sessions/${sid}`, { method: "DELETE" });
}

export function fetchPage(
  instance: string,
  pageId: string,
): Promise<SynapsePage> {
  return api(`/${instance}/fetch_by_id?id=${encodeURIComponent(pageId)}`);
}

/** Resolve an `entity:<type>/<slug>` ref to its synapse page, or null when the
 *  entity has no dedicated page (a reference-only tag). */
export async function resolveEntity(
  instance: string,
  entityRef: string,
): Promise<SynapsePage | null> {
  try {
    return await api(`/${instance}/resolve_entity?entity_ref=${encodeURIComponent(entityRef)}`);
  } catch {
    return null;   // 404 → no entity page; chip stays a plain tag
  }
}

/** Returns the URL for the SSE ask endpoint — POST + body=question. */
export function askUrl(instance: string, sid: string): string {
  return `${API}/${instance}/sessions/${sid}/ask`;
}

/** URL for a raw corpus blob (R2-backed, served by the Worker). */
export function blobUrl(instance: string, corpusPath: string): string {
  return `${API}/${instance}/blob?path=${encodeURIComponent(corpusPath)}`;
}

/** Opaque, stable corpus ref = sha256("<instance>/<uri>")[:32]. Matches the
 * Worker's resolver so the raw path never appears in the URL (no enumeration). */
export async function corpusHash(instance: string, uri: string): Promise<string> {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(`${instance}/${uri}`));
  return [...new Uint8Array(buf)].map(b => b.toString(16).padStart(2, "0")).join("").slice(0, 32);
}

/** Secure corpus download URL — the Worker RBAC-checks then streams from R2. */
export function corpusUrl(instance: string, hash: string): string {
  return `${API}/${instance}/corpus/${hash}`;
}

/** Resolve a source `uri` to its link: external URL as-is; otherwise the opaque
 * RBAC-gated corpus URL. Used to render synapse-page source hyperlinks. */
export async function sourceHref(instance: string, uri: string): Promise<string> {
  if (/^https?:\/\//i.test(uri)) return uri;
  return corpusUrl(instance, await corpusHash(instance, uri));
}

/** A page's raw source, resolved by the Worker: R2 corpus link (prose),
 *  GitHub file view (code-archetype), or the external URL as-is. */
export interface SourceLink {
  uri: string;
  kind: "github" | "r2" | "external";
  url: string;
  label: string;
}
export interface SourceLinksResponse {
  instance: string;
  page_id: string;
  archetype: "prose" | "code";
  sources: SourceLink[];
}
export function getSourceLinks(instance: string, pageId: string): Promise<SourceLinksResponse> {
  return api(`/${instance}/source-link?page=${encodeURIComponent(pageId)}`);
}

/* ---------- members (H — owner-managed) ---------- */

export type MemberRole = "owner" | "admin" | "writer" | "member";
/** The roles an owner can grant. */
export type GrantableRole = "member" | "writer" | "admin";

export interface Member {
  email: string;
  instance_id: string;
  role: MemberRole;
  invited_by: string | null;
  created_at: string;
}

export function listMembers(instance: string): Promise<{ instance: string; you: MemberRole | null; members: Member[] }> {
  // DEV-only sample roster for the ?devauth preview (stripped from prod builds).
  if (import.meta.env.DEV && new URLSearchParams(location.search).has("devauth")) {
    return Promise.resolve({
      instance, you: "owner" as MemberRole, members: [
        { email: "owner@local", instance_id: instance, role: "owner", invited_by: null, created_at: "2026-06-07" },
        { email: "writer@local", instance_id: instance, role: "writer", invited_by: "owner@local", created_at: "2026-06-07" },
        { email: "member@local", instance_id: instance, role: "member", invited_by: "owner@local", created_at: "2026-06-07" },
      ],
    });
  }
  return api(`/${instance}/members`);
}

/** Invite a member. Omit `password` (the default) to email them a sign-in link
 *  they use to set their own; pass one to pre-set it. */
export function inviteMember(instance: string, email: string, role: GrantableRole, password?: string): Promise<{ ok: boolean; email: string; role: string; invited: boolean }> {
  const body: Record<string, unknown> = { email, role };
  if (password) body.password = password;
  return api(`/${instance}/members/invite`, { method: "POST", body: JSON.stringify(body) });
}

export function elevateMember(instance: string, email: string, role: GrantableRole): Promise<{ ok: boolean; email: string; role: string }> {
  return api(`/${instance}/members/elevate`, { method: "POST", body: JSON.stringify({ email, role }) });
}

export function removeMember(instance: string, email: string): Promise<{ ok: boolean; removed: string }> {
  return api(`/${instance}/members/remove`, { method: "POST", body: JSON.stringify({ email }) });
}


/* ---------- API clients (admin-managed) ---------- */

export type ClientScope = "mcp:read" | "mcp:tools";

export interface ApiClient {
  client_id: string;
  client_name: string;
  scope: string;
  created_at: string;
  created_by: string | null;
  revoked_at: string | null;
}

/** The create and rotate responses: the client plus its secret, shown once. */
export interface ApiClientSecret extends ApiClient {
  client_secret: string;
  mcp_endpoint: string;
  retrieve_endpoint: string;
}

export function listApiClients(instance: string): Promise<{ instance: string; clients: ApiClient[] }> {
  return api(`/${instance}/admin/clients`);
}

export function createApiClient(instance: string, client_name: string, scope: string): Promise<ApiClientSecret> {
  return api(`/${instance}/admin/clients`, { method: "POST", body: JSON.stringify({ client_name, scope }) });
}

export function rotateApiClientSecret(instance: string, clientId: string): Promise<ApiClientSecret> {
  return api(`/${instance}/admin/clients/${clientId}/rotate-secret`, { method: "POST" });
}

export function revokeApiClient(instance: string, clientId: string): Promise<ApiClient> {
  return api(`/${instance}/admin/clients/${clientId}`, { method: "DELETE" });
}


/** Instance-declared Felix pipeline graphs (committed under instances/<inst>/pipelines/). */
export function listPipelines(instance: string): Promise<{ instance: string; pipelines: unknown[] }> {
  return api(`/${instance}/pipelines`);
}
